<template>
	<div
		class="header_section_header sticky-header"
		:class="compact ? 'is-compact' : 'is-wrap'">
		<div class="header_left">
			<div class="header_title">{{ title }}</div>
			<div class="header_description mb-4" v-if="description">
				{{ description }}
			</div>

			<!-- Sub-Navigation Filters: Mensual / Trimestral / YTD / Anual.
				Cuando el tab habilita range, el AnualCalendar vive inline a la
				derecha del grupo — vive independiente de los períodos (no
				participa del `is-active`), pero comparte fila para que el
				usuario lo lea como parte del cluster de "controles del período
				analizado". -->
			<div class="period_filters_row">
				<div class="sub_nav_filters">
					<button
						v-for="period in filterOptions"
						:key="period.id"
						class="filter_btn"
						:class="{ 'is-active': filterPeriod === period.id }"
						@click="$emit('update-filter-period', period.id)">
						{{ period.label }}
					</button>
				</div>
				<AnualCalendar v-if="calendarMode === 'range' && ['month', 'quarter'].includes(filterPeriod)"
					class="range_inline_calendar"
					mode="range"
					:range-value="dateRange"
					range-trigger-label="Rango"
					:disableNewerDates="true"
					:disabledFrom="getNow"
					:alignRight="false"
					@range-change="(range) => $emit('update-date-range', range)" />
			</div>
		</div>

		<!-- Slot opcional para sub-filters extra (Cuentas/Clientes en Ventas,
			MultiSelectDropdown de contrapartes en Gastos/Costos/Activos/NoOper).
			El padre define markup y eventos — TabHeader no asume estructura. -->
		<div v-if="$slots['center-sub-nav']" class="center_sub_nav">
			<slot name="center-sub-nav" />
		</div>

		<div class="right_actions is-flex">
			<!-- Single-mode: el calendar vive en right_actions (todos los tabs
				menos Ventas). Range-mode: ya se renderizó inline al lado de
				los chips de período arriba; este slot queda sin calendar. -->
			<div v-if="calendarMode !== 'range'"
				class="calendar_picker is-flex is-align-items-center mr-3">
				<AnualCalendar
					class="calendar"
					:value="endMonth"
					@date-change="(date) => $emit('update-end-month', date)"
					:disableNewerDates="true"
					:disabledFrom="getNow"
					formatLabelDate="MMMM YYYY"
					:alignRight="true" />
			</div>
			<button class="theme_btn_white is-flex is-align-items-center">
				<DownloadFile class="mr-2" style="width: 16px; height: 16px;" />
				Exportar
			</button>
		</div>
	</div>
</template>

<script>
import AnualCalendar from "@/components/classification/AnualCalendar";
import DownloadFile from "@/components/svg/DownloadFile.vue";

export default {
	name: "TabHeader",
	components: { AnualCalendar, DownloadFile },
	props: {
		title: { type: String, required: true },
		description: { type: String, default: "" },
		// Valor activo del set de períodos (Mensual/Trimestral/YTD/Anual).
		filterPeriod: { type: String, required: true },
		filterOptions: { type: Array, required: true },
		endMonth: { type: Date, required: true },
		// getNow es el corte "ahora" pasado al AnualCalendar — los tabs lo
		// expone como computed que devuelve `moment().toDate()`, por eso
		// llega como Date (no Function).
		getNow: { type: Date, required: true },
		// Compact = sin wrap, header en una sola línea. ResultadosTab y
		// RentabilidadTab no tienen `center_sub_nav` y caben en una fila.
		// El resto (Ventas/Gastos/Costos/Activos/NoOper) hace wrap para
		// que el sub-nav baje a una segunda fila cuando no entra horizontal.
		compact: { type: Boolean, default: false },
		// Modo del calendario del header. "single" (default) = picker de mes
		// único (todos los tabs salvo Ventas). "range" = picker de rango de
		// meses (Ventas — el usuario elige [from, to] explícito y eso
		// reemplaza el dropdown de "Último año / 24 meses / ..."). El padre
		// pasa `dateRange` cuando el modo es "range".
		calendarMode: {
			type: String,
			default: "single",
			validator: (v) => ["single", "range"].includes(v),
		},
		dateRange: { type: Object, default: null },
	},
};
</script>

<style scoped lang="scss">
.header_section_header {
	// Ancla directo al header global (publicado en Sidebar.vue). Sin
	// NavigationTabs intermedia que medir, no necesitamos --tab-sticky-top.
	position: sticky;
	top: var(--app-chrome-top, 0px);
	z-index: 4;
	background-color: var(--report-bg);
	border-bottom: 1px solid var(--report-border);
	padding-bottom: 8px;
	// margin-bottom: 1.5rem;
}

.is-compact {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: flex-end;
}

.is-wrap {
	display: flex;
	flex-wrap: wrap;
	gap: 16px 24px;
	align-items: flex-end;

	.header_left { display: flex; flex-direction: column; }
	.center_sub_nav {
        margin-left: auto;
        margin-right: auto;
    }
	.right_actions { margin-left: auto; }

	@media (max-width: 600px) {
		.center_sub_nav, .right_actions { margin-left: 0; }
	}
}

.header_title {
	font-size: 32px;
	font-weight: 700;
	color: var(--report-text);
	margin-bottom: 4px;
}

.header_description {
	color: var(--report-text-muted);
	font-size: 14px;
}

.period_filters_row {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.range_inline_calendar {
	// El calendar usa block layout adentro (.custom-dropdown wraps absolute).
	// Acá lo dejamos como inline-block para que el dropdown anclado se
	// posicione respecto al chip, no respecto al contenedor flex.
	display: inline-block;
}

.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-surface);
	}

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}

.calendar_picker {
	background: var(--report-primary);
	color: white;
	border-radius: 8px;
	font-weight: 500;

	:deep(.calendar-label) {
		color: white !important;
	}
}

.theme_btn_white {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 8px 16px;
	font-weight: 500;
	color: var(--report-text-strong-2);
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	cursor: pointer;

	&:hover {
		background: var(--report-surface);
	}
}
</style>
